import type {
  OfferPageFilterType,
  OfferPagePriceMode,
  OfferPageQuickFilters,
  OfferPageRoom,
  OfferPageState,
} from 'contexts/OfferPage/offerPageStateReducer'
import type { RoomRateViews } from 'components/OfferPage/Property/singlePropertyUtils'
import type { FlashPackageCharterFallbackPricingResult } from 'components/OfferPage/FlashHotel/FlashPackageCharterFallbackPricingRegistration'
import type { FareClassWithReward } from 'constants/flight'
import type { CheapestAvailableReward } from 'hooks/OfferPage/useCheapestAvailableReward'
import type { LoyaltyEarnTotals } from 'loyalty/hooks/useGroupedLoyaltyEarnTotals'
import type { OfferPageLoyaltyEarnCalculationStatus } from './useOfferPageRoomPackageLoyaltyEarnTotals'
import { onlyDefinedProperties } from 'lib/object/objectUtils'
import type { IntlShape } from 'react-intl'
import type {
  SurfaceAtomRequest,
  SurfaceRecentCommittedStateRequest,
} from 'tripPlanner/assistant/api/surfaceSnapshotContract'
import { normalizeAssistantRoomOccupancy } from 'tripPlanner/assistant/utils/assistantRoomOccupancyText'
import { nonEmptyString } from './offerPageContextFormatting'

export interface OfferPageSnapshotRoom {
  id: string
  adults: number
  children?: number
  teenagers?: number
  infants?: number
  childrenAge?: Array<number>
  roomId?: string
  rateId?: string
}

export interface OfferPageSnapshotSelection {
  activeFilter?: OfferPageFilterType
  activeSubFilter?: OfferPageFilterType
  activeHeaderFilter?: OfferPageFilterType
  airportCode?: string
  checkInDate?: string
  checkOutDate?: string
  duration: number
  durationSelected?: boolean
  flights: boolean
  isMultiRoomConnected?: boolean
  multiRoomMode: OfferPageState['multiRoomMode']
  isMultiRoomSelectRooms?: boolean
  displayMemberPrice?: boolean
  selectedBedbankRoomId?: string
  selectedPackageKey?: string
  propertyRoomId?: string
  roomOption?: {
    roomRateId: string
    roomTypeId: string
  }
  quickFilters?: Partial<OfferPageQuickFilters>
  isAlternativeDatesFlow?: boolean
  fareClass: OfferPageState['fareClass']
  priceMode: OfferPagePriceMode
  pointsToApply?: number
  pointsHideZeroCash?: boolean
  viewedRateIdByRoomId?: Record<string, string>
  rooms: Array<OfferPageSnapshotRoom>
}

export interface OfferPageSnapshotPriceFormatContext {
  currencyCode: string
  regionCode: string
}

interface OfferPageSnapshotSourceBase {
  key: string
  selection: OfferPageSnapshotSelection
  priceFormatContext: OfferPageSnapshotPriceFormatContext
  pageUrl?: string
  /** Only set via `withOfferPageSnapshotIntl` at the turn-authority call site, so a cancellation-text helper can localise instead of falling back to English `defaultMessage`s. */
  intl?: IntlShape
  /**
   * Loyalty earn totals keyed by rate ID (property/bedbank) or package unique key (flash) --
   * a stable key rather than array position, since a room can have a variable number of rates.
   */
  roomPackageLoyaltyEarnTotals?: Record<string, LoyaltyEarnTotals | undefined>
  /** Aggregate status of `roomPackageLoyaltyEarnTotals`'s underlying calculations -- surfaced as the `packages.loyalty_status` manifest fact, mirroring `packages.rates_status`. */
  loyaltyStatus?: OfferPageLoyaltyEarnCalculationStatus
  /**
   * Whether the LuxPlus+ program is enabled at all -- gates the member-only/early-access banner
   * fact the same way `FlashHotelContent.tsx`'s own `<LuxPlusNotificationBanner>` is gated.
   * Shared across every variant (not just Flash) since `OfferBase.luxPlus` -- and the hidden-offer
   * redirect gate that enforces it (`getIsLuxPlusHiddenOffer`) -- both apply variant-agnostically.
   */
  isLuxPlusEnabled?: boolean
  /**
   * The offer-level LuxPlus+ access restriction, pre-resolved by each container from whichever
   * underlying offer object is actually authoritative for that variant (e.g. Property/SPP's own
   * `lpcOffer`-vs-`bedbankOffer` split) -- kept as one shared, pre-normalised field so the fact this
   * builds from stays identical across variants instead of drifting as LuxPlus+ member-only
   * coverage expands to more offer types over time.
   */
  luxPlusAccess?: App.OfferLuxPlusAccessType
}

export interface FlashOfferPageSnapshotSource extends OfferPageSnapshotSourceBase {
  variant: 'flash'
  offer: App.HotelOffer
  packageViews: Array<App.OfferPackageOptionView>
  /** The LuxPlus+ tier whose inclusions the customer can currently see, or undefined if LuxPlus+ is disabled/not applicable -- mirrors `useOfferInclusions`'s own gating. */
  luxPlusInclusionsTier?: App.MembershipSubscriptionTier
  /** The `croInclusionValidity` Optimizely flag -- gates stripping "valid for N guests" text from inclusions, matching the real tile. */
  isInclusionsValidityEnabled?: boolean
  /** Only set once a charter bundle is actually active -- matches `FlashHotelRateOption`'s `charterBundleRoute` prop. */
  charterBundleRoute?: App.CharterBundleRoute
  /**
   * Charter departure date when available. Passed separately from check-in so
   * `getFlashCancellationPolicyView` / `getOfferFlexibleCancellationReferenceDate` can use
   * departure only for charter cancellation policies (and hide while departure is resolving).
   */
  charterDepartDate?: string
  /** Mirrors `isCharterEnabled` on the real page — required for charter policy reference-date selection. */
  isCharterModeEnabled?: boolean
  /**
   * The real, live flight price the customer would actually pay (`getEffectiveHotelFlightPrice`,
   * matching `FlashHotelOptionPriceDetails`'s own `effectiveFlightPrice`) -- distinct from
   * `packageView.flightPrice`, a static calendar-level indicative sample fare. Only meaningful
   * once dates are picked; `undefined`/0 while unavailable.
   */
  effectiveFlightPrice?: number
  /** True while the live flight price above is still being fetched for the current selection. */
  effectiveFlightPriceLoading?: boolean
  /**
   * Mirrors `FlashHotelOptionPriceDetails`'s own `shouldUseFallbackPricing`: true when a charter
   * bundle is active but there's no check-in date, or the check-in date doesn't match an actual
   * charter departure date. In this state `effectiveFlightPrice` is always 0 (charter routes are
   * excluded from `getEffectiveHotelFlightPrice`'s cached-fare fallback), so the real page switches
   * to `useCharterBundleFallbackPricing`'s cheapest jointly-available hotel+flight date instead of
   * a live per-date fare -- this flag tells `buildOfferPageSnapshot.ts` to do the same rather than
   * silently reporting a hotel-only total.
   */
  shouldUseCharterFallbackPricing?: boolean
  /**
   * Per-package fallback pricing (only populated when `shouldUseCharterFallbackPricing` is true),
   * keyed by `packageView.package.uniqueKey` -- matches what a per-package
   * `useCharterBundleFallbackPricing({ packageUniqueKey, ... })` call would return for that package,
   * the same way the real page calls it once per rendered package/rate row. A package missing from
   * this map means its fallback price hasn't resolved yet (still fetching), not that it's $0.
   */
  charterFallbackPricingByPackage?: Record<string, FlashPackageCharterFallbackPricingResult>
  /**
   * Offer-wide fallback for the "Return for FREE with LuxPlus+" promo (MLE→MEL economy return priced at
   * $0) -- matches `useOfferPageFlightPrice`'s own `hasReturnFareFree`. `FlashHotelOptionPriceDetails.tsx`
   * and siblings prefer each package's own `packageView.hasReturnFareFree` (already present on
   * `packageViews` above) and only fall back to this offer-wide value when a package's own field is
   * `undefined` -- mirrored exactly in `buildOfferPageSnapshot.ts`. In fallback-pricing mode
   * (`shouldUseCharterFallbackPricing`), prefer `charterFallbackPricingByPackage[uniqueKey]`'s own
   * `hasReturnFareFree` instead -- this offer-wide hook-level value needs a resolved dated journey,
   * so it's always `false` while no check-in date is picked, even though the fallback-pricing path
   * has its own answer for that exact state.
   */
  hasReturnFareFree?: boolean
  /**
   * The selected feeder-origin airport (SYD/BNE/ADL), or undefined when flying from the route's
   * default origin (MEL) -- matches `getFeederOriginAirportCode`. Every real render site suppresses
   * the "Return for FREE with LuxPlus+" badge whenever a feeder origin is selected, regardless of
   * `hasReturnFareFree`'s value, so this is threaded through purely to replicate that suppression.
   */
  feederOriginAirportCode?: string
  /**
   * The confirmed charter flight itinerary for the current dates/fare class -- matches
   * `ReturnCharterFlightTile.tsx`'s own `useCharterFlightJourneysForHotelDates` call (same Redux
   * cache key as `effectiveFlightPrice` above, so this doesn't add a new fetch). Only meaningful
   * once both a check-in and check-out date are picked: with no dates, the underlying hook still
   * returns its first-available journey as a placeholder (the same way the real page's own
   * calendar-driven components do before a date is chosen), which would misrepresent an arbitrary
   * date as "the" itinerary -- `buildOfferPageSnapshot.ts` only surfaces this once
   * `selection.checkInDate`/`checkOutDate` are both set, matching `ReturnCharterFlightTile`'s own
   * `!hasFlights` empty state otherwise.
   */
  charterItinerary?: FlashCharterItinerary
  /**
   * The full set of selectable fare classes (base cabins + Société Rewards variants when reward
   * seats are enabled) -- matches `useCharterFareClassPolicy` + `useMaldivesCharterModalFareClasses`,
   * the exact hooks the real "Change Class" fare-tier modal uses to decide its own card set. Both
   * are pure derivations (campaign tags + occupancy math + feature flags, no fetch), so this costs
   * nothing to compute. Deliberately excludes live cross-cabin cash/reward pricing: that data is
   * only ever fetched by the real page once a customer opens that modal (a distinct
   * fare-families/upsell + rewards-availability request per cabin), so pre-fetching it here would
   * mean up to ~8 new network calls the modal itself may never trigger. `buildOfferPageSnapshot.ts`
   * only builds the static catalog (cabin names, inclusions, baggage) from this list.
   */
  charterSelectableFareClasses?: ReadonlyArray<FareClassWithReward>
  /**
   * The cheapest Société Rewards seat redemption the customer can actually afford (points balance
   * included), across the offer's reward-eligible fare classes -- matches
   * `FlashHotelRewardsAvailableCard`'s own `useCheapestAvailableReward` call exactly (same params),
   * and that card is mounted unconditionally on the real page regardless of the assistant, so this
   * shares its cache rather than adding a new fetch. `undefined` when the customer is already on a
   * reward fare (nothing left to upsell), not logged in / no loyalty account, no reward inventory
   * fits their points balance, or dates aren't picked yet -- matching the real card's own
   * `isRewardFareClass(fareClass)` / `!cheapest` suppression exactly.
   */
  charterCheapestAvailableReward?: CheapestAvailableReward
}

export interface FlashCharterItineraryLeg {
  departureAirportCode: string
  departureDateTime: string
  arrivalAirportCode: string
  arrivalDateTime: string
  isDirect: boolean
  durationHours: number
  durationMinutes: number
}

export interface FlashCharterItinerary {
  departing: FlashCharterItineraryLeg
  returning: FlashCharterItineraryLeg
}

export interface LmePropertyOfferPageSnapshotSource extends OfferPageSnapshotSourceBase {
  variant: 'lme_property'
  property: App.Property.Property
  roomRateViews: Array<RoomRateViews>
  ratesStatus?: 'loading' | 'unavailable'
  /** The `croInclusionValidity` Optimizely flag -- gates stripping "valid for N guests" text from LPC rate inclusions, matching the real tile. */
  isInclusionsValidityEnabled?: boolean
  /** The `croUrgencyOnSppRooms` Optimizely experiment -- gates the rooms-remaining urgency atom, matching `PropertyRoomOption`'s own gate. */
  isSppRoomUrgencyEnabled?: boolean
  /** Needed to resolve an LPC rate's package (for `roomPolicyDescription`) the same way `PropertyRateOption` does. */
  lpcOffer?: App.HotelOffer
}

export interface BedbankOfferPageSnapshotSource extends OfferPageSnapshotSourceBase {
  variant: 'bedbank'
  offer: App.BedbankOffer
  packageViews: Array<App.BedbankOfferPackageView>
  /** Whether the "Pay at property" feature flag is enabled for this page view. */
  isPropertyCollectEnabled: boolean
  /** Mirrors `offerFlightsEnabled`: whether flight bundling is offered at all for this booking. */
  flightsEnabled: boolean
}

export interface SppPropertyOfferPageSnapshotSource extends OfferPageSnapshotSourceBase {
  variant: 'spp_property'
  property: App.Property.Property
  roomRateViews: Array<RoomRateViews>
  ratesStatus?: 'loading' | 'unavailable'
  /** The `croInclusionValidity` Optimizely flag -- gates stripping "valid for N guests" text from LPC rate inclusions. Always a no-op for this variant (bedbank-backed rates are never `lpc`-sourced), kept only so the shared lme_property/spp_property builder code doesn't need a runtime type guard. */
  isInclusionsValidityEnabled?: boolean
  /** The `croUrgencyOnSppRooms` Optimizely experiment -- gates the rooms-remaining urgency atom, matching `PropertyRoomOption`'s own gate. */
  isSppRoomUrgencyEnabled?: boolean
  /** Always undefined for this variant -- see `isInclusionsValidityEnabled`'s comment. */
  lpcOffer?: App.HotelOffer
}

export type OfferPageAssistantSource =
  | FlashOfferPageSnapshotSource
  | LmePropertyOfferPageSnapshotSource
  | BedbankOfferPageSnapshotSource
  | SppPropertyOfferPageSnapshotSource

export interface OfferPageSnapshotContribution {
  key: string
  sourcePath: string
  visibleState?: {
    activeControl?: string
    activeDrawer?: string
    activeModal?: string
    suppressAutoFocus: boolean
  }
  recentCommittedState: Array<SurfaceRecentCommittedStateRequest>
  atoms: Array<SurfaceAtomRequest>
}

export interface OfferPageSnapshotInput {
  source: OfferPageAssistantSource
  contributions: Array<OfferPageSnapshotContribution>
}

export function withOfferPageSnapshotPageUrl(
  input: OfferPageSnapshotInput,
  pageUrl: string,
): OfferPageSnapshotInput {
  return { ...input, source: { ...input.source, pageUrl } }
}

export function withOfferPageSnapshotIntl(
  input: OfferPageSnapshotInput,
  intl: IntlShape,
): OfferPageSnapshotInput {
  return { ...input, source: { ...input.source, intl } }
}

function copyRoom(room: OfferPageRoom): OfferPageSnapshotRoom {
  // Canonicalise sparse assistant storage; age categorisation runs at format time with offer thresholds.
  const occupancy = normalizeAssistantRoomOccupancy(room)

  return onlyDefinedProperties({
    id: room.id,
    adults: occupancy.adults,
    children: occupancy.children,
    teenagers: occupancy.teenagers,
    infants: occupancy.infants,
    childrenAge: occupancy.childrenAge?.length ? [...occupancy.childrenAge] : undefined,
    roomId: room.roomId,
    rateId: room.rateId,
  })
}

export function createOfferPageSnapshotSelection(
  state: OfferPageState,
): OfferPageSnapshotSelection {
  return onlyDefinedProperties({
    activeFilter: state.activeFilter,
    activeSubFilter: state.activeSubFilter,
    activeHeaderFilter: state.activeHeaderFilter,
    airportCode: nonEmptyString(state.airport?.code),
    checkInDate: state.checkInDate,
    checkOutDate: state.checkOutDate,
    duration: state.duration,
    durationSelected: state.durationSelected,
    flights: state.flights,
    isMultiRoomConnected: state.isMultiRoomConnected,
    multiRoomMode: state.multiRoomMode,
    isMultiRoomSelectRooms: state.isMultiRoomSelectRooms,
    displayMemberPrice: state.displayMemberPrice,
    selectedBedbankRoomId: state.selectedBedbankRoomId,
    selectedPackageKey: state.selectedPackageKey,
    propertyRoomId: state.propertyRoomId,
    roomOption: state.roomOption ? {
      roomRateId: state.roomOption.roomRateId,
      roomTypeId: state.roomOption.roomTypeId,
    } : undefined,
    quickFilters: state.quickFilters !== undefined ? { ...state.quickFilters } : undefined,
    isAlternativeDatesFlow: state.isAlternativeDatesFlow,
    fareClass: state.fareClass,
    priceMode: state.priceMode,
    pointsToApply: state.pointsToApply,
    pointsHideZeroCash: state.pointsHideZeroCash,
    viewedRateIdByRoomId: state.viewedRateIdByRoomId ? { ...state.viewedRateIdByRoomId } : undefined,
    rooms: state.rooms.map(copyRoom),
  })
}
